import { useEffect, useState } from "react";

/**
 * Shared collapsible-phase behaviour for the Journey / Phases & tasks
 * accordion. Mirrors the mobile app's expected UX so behaviour is identical
 * across Ops Portal (iPad / Web) and Mobile:
 *
 *   • Default: fully collapsed. UAT corrective pass — the Engineer
 *     workflow expects to open explicitly, not to land on an auto-open
 *     phase. Ops manages its own local `phasesCollapsed` state, so this
 *     change only affects the Engineer surface. Consumers that need the
 *     old "auto-open current phase" behaviour can call `expandAll()` or
 *     `toggle(currentId)` right after mount.
 *   • Each phase toggles independently (multi-open, matches Documents /
 *     Appointments sections).
 *   • State is keyed by phase id — safe to remount when the project loads.
 *
 * Consumers:
 *   const { isOpen, toggle } = usePhaseCollapse(project?.phases);
 *   ...
 *   <button onClick={() => toggle(phase.id)}>…</button>
 *   {isOpen(phase.id) && <TaskList/>}
 */
export function usePhaseCollapse(phases) {
    const [openIds, setOpenIds] = useState(() => new Set());
    const [sectionOpen, setSectionOpen] = useState(false);

    // Reset to a clean collapsed default whenever the phase-id list
    // changes. We re-key on the joined id list so a freshly loaded
    // project always starts from a clean default.
    const key = (phases || []).map((p) => p.id).join("|");

    useEffect(() => {
        setOpenIds(new Set());
    }, [key]); // eslint-disable-line react-hooks/exhaustive-deps

    const isOpen = (id) => openIds.has(id);
    const toggle = (id) =>
        setOpenIds((prev) => {
            const next = new Set(prev);
            if (next.has(id)) next.delete(id);
            else next.add(id);
            return next;
        });
    const expandAll = () => setOpenIds(new Set((phases || []).map((p) => p.id)));
    const collapseAll = () => setOpenIds(new Set());
    const allExpanded = phases && phases.length > 0 && openIds.size === phases.length;

    return {
        isOpen, toggle, expandAll, collapseAll, allExpanded,
        sectionOpen, toggleSection: () => setSectionOpen((v) => !v),
    };
}
